import React, { useState } from "react";
import { db } from "../firebase-config";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

const regions = [
  "Budapest",
  "Pest megye",
  "Fejér megye",
  "Komárom-Esztergom megye",
  "Veszprém megye",
  "Győr-Moson-Sopron megye",
  "Vas megye",
  "Zala megye",
  "Baranya megye",
  "Somogy megye",
  "Tolna megye",
  "Borsod-Abaúj-Zemplén megye",
  "Heves megye",
  "Nógrád megye",
  "Hajdú-Bihar megye",
  "Jász-Nagykun-Szolnok megye",
  "Szabolcs-Szatmár-Bereg megye",
  "Bács-Kiskun megye",
  "Békés megye",
  "Csongrád-Csanád megye"
];

const structures = [
  { id: "fa", name: "Fa szerkezet", price: "390.000 Ft/m²-től" },
  { id: "blokk", name: "Blokk (viablokk)", price: "440.000 Ft/m²-től" },
  { id: "tegla", name: "Tégla", price: "490.000 Ft/m²-től" }
];

export default function EpitkezesiLeadForm({ onSuccess, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    hasLand: "",
    region: "",
    structure: "",
    area: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.hasLand || !formData.region || !formData.structure) {
      setError("Kérjük, töltsd ki az összes kötelező mezőt!");
      setLoading(false);
      return;
    }

    try {
      // Save to Firebase - construction_leads collection
      await addDoc(collection(db, "construction_leads"), {
        ...formData,
        area: formData.area ? parseInt(formData.area) : null,
        status: "new", // new, qualified, assigned, meeting_scheduled, offer_sent, contracted, closed, not_relevant
        assignedPartner: null, // Will be assigned by admin
        assignedPartnerId: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        source: "website",
        leadType: "construction",
        notes: [],
        meetings: [],
        qualificationData: null
      });

      setSuccess(true);
      if (onSuccess) onSuccess();
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: "",
          email: "",
          phone: "",
          hasLand: "",
          region: "",
          structure: "",
          area: "",
          message: ""
        });
        setSuccess(false);
        if (onClose) onClose();
      }, 3000);

    } catch (err) {
      console.error("Error submitting lead:", err);
      setError("Hiba történt a küldés során. Kérjük, próbáld újra!");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-8 text-center">
        <div className="text-5xl mb-4">✅</div>
        <h3 className="text-xl font-bold text-white mb-2">Köszönjük az érdeklődésedet!</h3>
        <p className="text-gray-400">
          Kollégánk hamarosan felveszi veled a kapcsolatot az előminősítés első lépéseként.
        </p>
        <p className="text-gray-500 text-sm mt-4">
          A HOPPhomes minden ügyfelet saját rendszerében kezel, és a projekt jellegéhez leginkább illeszkedő kivitelezőt választja ki.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Header */}
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-white mb-2">Szeretnék építkezni</h3>
        <p className="text-gray-400 text-sm">
          Töltsd ki az alábbi űrlapot, és kollégánk felveszi veled a kapcsolatot.
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Name */}
      <div>
        <label className="block text-gray-300 text-sm font-medium mb-1">
          Név <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-secondary"
          placeholder="Teljes neved"
          required
        />
      </div>

      {/* Email & Phone */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-300 text-sm font-medium mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-secondary"
            placeholder="email@example.com"
            required
          />
        </div>
        <div>
          <label className="block text-gray-300 text-sm font-medium mb-1">
            Telefon <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-secondary"
            placeholder="+36 30 123 4567"
            required
          />
        </div>
      </div>

      {/* Has Land */}
      <div>
        <label className="block text-gray-300 text-sm font-medium mb-2">
          Van már telked? <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="hasLand"
              value="yes"
              checked={formData.hasLand === "yes"}
              onChange={handleChange}
              className="w-4 h-4 accent-secondary"
            />
            <span className="text-white">Igen, van telkem</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="hasLand"
              value="no"
              checked={formData.hasLand === "no"}
              onChange={handleChange}
              className="w-4 h-4 accent-secondary"
            />
            <span className="text-white">Még nincs</span>
          </label>
        </div>
      </div>

      {/* Region */}
      <div>
        <label className="block text-gray-300 text-sm font-medium mb-1">
          Régió / Megye <span className="text-red-500">*</span>
        </label>
        <select
          name="region"
          value={formData.region}
          onChange={handleChange}
          className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-secondary"
          required
        >
          <option value="">Válassz régiót...</option>
          {regions.map(region => (
            <option key={region} value={region}>{region}</option>
          ))}
        </select>
      </div>

      {/* Structure Preference */}
      <div>
        <label className="block text-gray-300 text-sm font-medium mb-2">
          Szerkezeti preferencia <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {structures.map(structure => (
            <label
              key={structure.id}
              className={`flex flex-col p-4 rounded-xl border cursor-pointer transition-all ${
                formData.structure === structure.id
                  ? "border-secondary bg-secondary/10"
                  : "border-white/10 bg-[#2a2a2a] hover:border-white/30"
              }`}
            >
              <input
                type="radio"
                name="structure"
                value={structure.id}
                checked={formData.structure === structure.id}
                onChange={handleChange}
                className="sr-only"
              />
              <span className="font-medium text-white">{structure.name}</span>
              <span className="text-xs text-gray-400 mt-1">{structure.price}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Area */}
      <div>
        <label className="block text-gray-300 text-sm font-medium mb-1">
          Tervezett alapterület (m²)
        </label>
        <input
          type="number"
          name="area"
          value={formData.area}
          onChange={handleChange}
          className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-secondary"
          placeholder="pl. 120"
          min="30"
          max="1000"
        />
      </div>

      {/* Message */}
      <div>
        <label className="block text-gray-300 text-sm font-medium mb-1">
          Megjegyzés
        </label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={3}
          className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-secondary resize-none"
          placeholder="Bármilyen kérdésed vagy megjegyzésed..."
        />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-secondary hover:bg-hover text-white font-bold py-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Küldés...
          </span>
        ) : (
          "👉 Ajánlatkérés elküldése"
        )}
      </button>

      {/* Trust note */}
      <p className="text-center text-gray-500 text-xs mt-4">
        🔒 Az adataidat bizalmasan kezeljük. A HOPPhomes kiválasztja a projektedhez leginkább illeszkedő kivitelezőt.
      </p>
    </form>
  );
}
